import React, { useRef, useEffect } from "react";

interface PlayerJSEvents {
  ready: () => void;
  play: () => void;
  error: () => void;
  timeupdate: (data: { currentTime: number; duration: number }) => void;
}

interface PlayerJS {
  on: <K extends keyof PlayerJSEvents>(
    event: K,
    callback: PlayerJSEvents[K]
  ) => void;
  off: <K extends keyof PlayerJSEvents>(event: K) => void;
}

interface BunnyStreamPlayerProps {
  videoId: string;
  onPlay: () => void;
  onError: () => void;
  onProgress: (percent: number) => void;
}




export function BunnyStreamPlayer({
  videoId,
  onPlay,
  onError,
  onProgress,
}: BunnyStreamPlayerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<PlayerJS | null>(null);

  // Store callbacks in refs so useEffect doesn't re-run when parent re-renders
  const onPlayRef = useRef(onPlay);
  const onErrorRef = useRef(onError);
  const onProgressRef = useRef(onProgress);
  onPlayRef.current = onPlay;
  onErrorRef.current = onError;
  onProgressRef.current = onProgress;

  useEffect(() => {
    let mounted = true;

    const initPlayer = () => {
      if (!iframeRef.current || !mounted) return;
      playerRef.current = new window.playerjs.Player(iframeRef.current);

      playerRef.current.on("ready", () => {
        if (!mounted) return;
        console.log("Bunny Player is ready");
        // With autoplay=true, the video is already playing when ready fires,
        // so the 'play' event has already been emitted and missed.
        onPlayRef.current();

        playerRef.current?.on("error", () => {
          onErrorRef.current();
        });

        playerRef.current?.on("timeupdate", (data) => {
          if (data.duration) {
            const percent = (data.currentTime / data.duration) * 100;
            onProgressRef.current(percent);
          }
        });
      });
    };

    // If Player.js is already loaded (from a previous test), initialize immediately
    if (window.playerjs) {
      initPlayer();
    } else {
      const script = document.createElement("script");
      script.src = "https://assets.mediadelivery.net/playerjs/player-0.1.0.min.js";
      script.async = true;
      document.body.appendChild(script);
      script.onload = initPlayer;
    }

    return () => {
      mounted = false;
      try {
        if (playerRef.current) {
          playerRef.current.off("ready");
          playerRef.current.off("play");
          playerRef.current.off("error");
          playerRef.current.off("timeupdate");
        }
      } catch (error) {
        // playerjs.off() can throw when the iframe is already disconnected
        console.warn("BunnyStream cleanup error (safe to ignore):", error);
      }
      playerRef.current = null;
    };
  }, [videoId]);

  return (
    <div style={{ position: "relative", paddingTop: "56.25%" }}>
      <iframe
        ref={iframeRef}
        id={`bunny-stream-embed-${videoId}`}
        src={`https://iframe.mediadelivery.net/embed/427329/${videoId}?autoplay=true&loop=true&muted=true&preload=true&responsive=true&playsinline=true`}
        style={{
          border: "none",
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: "100%",
        }}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen; playsinline;"
      />
    </div>
  );
}
